import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) return { status: null, isBlocked: false, isPastDue: false, banner: null, refresh: () => {} };
  return ctx;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/me/subscription-status`, { withCredentials: true });
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Re-check every 2 minutes so the UI auto-locks if a webhook flips the status.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(refresh, 120000);
    return () => clearInterval(id);
  }, [user?.id, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    loading,
    subscription: data?.subscription || null,
    status: data?.subscription?.status || null,
    isBlocked: !!data?.is_blocked,
    isPastDue: !!data?.is_past_due,
    isSuperAdmin: !!data?.is_super_admin,
    banner: data?.banner || null,
    refresh,
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};
